/**
 * Network — Партнёрская структура (10 линий в глубину).
 *
 * Три секции:
 *
 *   1. HERO: суммарный доход с рефералки + количество открытых линий
 *      + реф-ссылка (или её серое состояние когда нет активного слота).
 *
 *   2. LINES: аккордеон из 10 линий. Каждая линия показывает:
 *      - процент выплат (5/3/2/1/1/... по ТЗ)
 *      - кол-во партнёров
 *      - их общий депозит
 *      - ваш доход с линии
 *      - статус (открыта / closed-grace / заблокирована)
 *      - Раскрытие показывает список ТОП-партнёров в этой линии.
 *
 *   3. STATUSES/RANKS: блок со статусом Imperator (если достигнут)
 *      и подсказкой что открывает дополнительные линии.
 *
 * Все карточки имеют InfoTooltip. Вся страница — read-only (real data
 * подключается в задаче 11 после JWT auth). Сейчас работает на
 * моковых данных которые отражают реальную структуру ответа от
 * будущего эндпоинта GET /api/luna/referrals/tree.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, type Variants, AnimatePresence } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Users,
  ChevronDown,
  Copy,
  Check,
  Lock,
  TrendingUp,
  Clock,
  Shield,
} from 'lucide-react';
import {
  GlassSurface,
  GlassPill,
  LiquidNumber,
  ClickableCard,
  InfoTooltip,
} from '@/components/glass';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useLunaProgress } from '@/hooks/useLunaProgress';
import {
  ProgressBars,
  loadProgressExpanded,
  saveProgressExpanded,
} from '@/components/nodes/ProgressBars';

// ────────────────────────────────────────────────────────────────────
// Реф-дерево Luna — данные приходят из GET /api/luna/referrals/tree
// ────────────────────────────────────────────────────────────────────
type LineStatus = 'active' | 'grace' | 'locked';

interface LineRow {
  level: number;        // 1-10
  pct: number;          // share percentage (5, 3, 2, 1, 1, ...)
  partners: number;
  depositUsdt: number;
  earnedUsdt: number;
  status: LineStatus;
  /** Сколько активных рефералов в 1-й линии нужно для открытия */
  threshold: number;
  /** When status=grace: days remaining before line permanently closes */
  graceDaysLeft?: number;
  /** Top partners (username/short id) for the accordion body */
  topPartners: Array<{ label: string; deposit: number; days: number }>;
}

// Percent per level — 10 уровней, источник истины — таблица
// luna_referral_config (БД). Здесь хардкод используется только
// как fallback до первого ответа API (после он заменяется
// реальными percent из ответа /referrals/tree).
// L1-5: 5 / 3 / 1.5 / 0.7 / 0.5  — открыты по факту инвайтов 0/1/3/5/8
// L6-10: 0.3 / 0.3 / 0.3 / 0.2 / 0.2 — открыты по 12/18/25/35/50
// Сумма = 12% (см. TZ §07).
const LINE_PCTS = [5, 3, 1.5, 0.7, 0.5, 0.3, 0.3, 0.3, 0.2, 0.2];

// Дефолтные значения (пока API не ответил)
let DEFAULT_HAS_ACTIVE_SLOT = false;
let DEFAULT_REF_LINK = '';

// ────────────────────────────────────────────────────────────────────
// Animation variants
// ────────────────────────────────────────────────────────────────────
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.06 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
  },
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────
export function Network() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<number | null>(1);
  const [copied, setCopied] = useState(false);

  // 3-барный виджет прогресса (Личный / Сеть / L1-30д) — реактивно
  // через SSE. Свёртывается по клику, состояние в localStorage.
  const { progress, loading: progressLoading } = useLunaProgress();
  const [progressExpanded, setProgressExpanded] = useState<boolean>(() =>
    loadProgressExpanded(),
  );
  const toggleProgress = () => {
    setProgressExpanded((v) => {
      const next = !v;
      saveProgressExpanded(next);
      return next;
    });
  };

  // Сколько активных рефов в 1-й линии — ключ к открытию линий.
  const [activeDirectRefs, setActiveDirectRefs] = useState(0);

  // Реальные данные из API. До ответа — пустые линии (нули),
  // а не мок-фейки с 190 партнёрами и 40140 оборотом.
  const [lines, setLines] = useState<LineRow[]>(() =>
    LINE_PCTS.map((pct, i) => ({
      level: i + 1,
      pct,
      partners: 0,
      depositUsdt: 0,
      earnedUsdt: 0,
      status: 'locked' as LineStatus,
      threshold: 0,
      topPartners: [],
    })),
  );
  const [HAS_ACTIVE_SLOT, setHasActiveSlot] = useState(DEFAULT_HAS_ACTIVE_SLOT);
  const [REF_LINK, setRefLink] = useState(DEFAULT_REF_LINK);

  useEffect(() => {
    import('@/services/api').then(({ lunaApi }) => {
      // Реф-дерево
      lunaApi.referrals.tree().then((res) => {
        const d = res.data as {
          success: boolean;
          lines?: Array<{
            level: number; percent: number; isOpen: boolean;
            refCount: number; earnings: string; deposits: string;
            threshold: number;
          }>;
          hasActiveSlot?: boolean;
          activeDirectRefs?: number;
        };
        if (d.success && d.lines) {
          setLines(d.lines.map((l) => ({
            level: l.level,
            pct: l.percent,
            partners: l.refCount,
            depositUsdt: parseFloat(l.deposits),
            earnedUsdt: parseFloat(l.earnings),
            status: (l.isOpen ? 'active' : 'locked') as LineRow['status'],
            threshold: l.threshold,
            topPartners: [], // Подробности загружаются при раскрытии линии
          })));
          setHasActiveSlot(d.hasActiveSlot ?? false);
          setActiveDirectRefs(d.activeDirectRefs ?? 0);
        }
      }).catch(() => { /* fallback mock */ });

      // Реф-ссылка
      lunaApi.referrals.link().then((res) => {
        const d = res.data as { success: boolean; link?: string };
        if (d.success && d.link) setRefLink(d.link);
      }).catch(() => {});
    });
  }, []);

  const totals = useMemo(() => {
    const open = lines.filter((l) => l.status === 'active').length;
    const grace = lines.filter((l) => l.status === 'grace').length;
    const partners = lines.reduce((s, l) => s + l.partners, 0);
    const earned = lines.reduce((s, l) => s + l.earnedUsdt, 0);
    const deposit = lines.reduce((s, l) => s + l.depositUsdt, 0);
    return { open, grace, partners, earned, deposit };
  }, [lines]);

  const handleToggle = (level: number) => {
    haptic.light();
    setExpanded((prev) => (prev === level ? null : level));
  };

  // Ленивая загрузка топ-партнёров при раскрытии линии.
  // Не тащим всех сразу — только ту линию которую юзер открыл.
  useEffect(() => {
    if (expanded == null) return;
    const level = expanded;
    const line = lines.find((l) => l.level === level);
    if (!line || line.partners === 0 || line.topPartners.length > 0) return;

    import('@/services/api').then(({ lunaApi }) => {
      lunaApi.referrals
        .teamPartners({ line: level, limit: 5, sortBy: 'turnover' })
        .then((res) => {
          const d = res.data as {
            success: boolean;
            partners?: Array<{
              id: number;
              username: string | null;
              firstName: string | null;
              totalStaked: string;
              joinedAt: string;
            }>;
          };
          if (!d.success || !d.partners) return;
          const now = Date.now();
          const topPartners = d.partners.map((p) => ({
            label: p.username
              ? `@${p.username}`
              : (p.firstName || `user#${p.id}`),
            deposit: parseFloat(p.totalStaked) || 0,
            days: Math.max(
              0,
              Math.floor((now - new Date(p.joinedAt).getTime()) / 86_400_000),
            ),
          }));
          setLines((prev) =>
            prev.map((l) => (l.level === level ? { ...l, topPartners } : l)),
          );
        })
        .catch(() => { /* silent */ });
    });
  }, [expanded, lines]);

  const handleCopy = () => {
    if (!HAS_ACTIVE_SLOT) {
      haptic.warning();
      return;
    }
    void navigator.clipboard.writeText(REF_LINK).then(() => {
      haptic.success();
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <motion.div
      className="space-y-5 max-w-md mx-auto pt-2 pb-6"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {/* ─── HERO ─── */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" className="p-6 relative overflow-hidden">
          {/* Decorative gradient blob */}
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: -60,
              right: -60,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, color-mix(in oklab, var(--accent-secondary) 40%, transparent) 0%, transparent 65%)',
              opacity: 0.7,
            }}
          />

          <div className="relative flex items-start justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1 font-mono">
                {t('network.structure')}
              </p>
              <h1 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight">
                {t('network.depth10')}
              </h1>
            </div>
            <div className="flex flex-col items-end gap-2">
              <InfoTooltip
                title={t('tooltips.networkStructure.title')}
                body={<Trans i18nKey="tooltips.networkStructure.bodyHTML" components={{ b: <strong /> }} />}
                bullets={[
                  t('tooltips.networkStructure.bullet1'),
                  t('tooltips.networkStructure.bullet2'),
                  t('tooltips.networkStructure.bullet3'),
                  t('tooltips.networkStructure.bullet4'),
                  t('tooltips.networkStructure.bullet5'),
                ]}
                tone="plasma"
                placement="inline"
                ariaLabel={t('network.lineToTooltipAria')}
              />
              <GlassPill tone="plasma" dot compact className="!mt-[10px]">
                {t('network.openCount', { open: totals.open })}
              </GlassPill>
            </div>
          </div>

          <div className="mb-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1 font-mono">
              {t('network.income')}
            </p>
            <LiquidNumber
              value={totals.earned}
              decimals={2}
              suffix="USD₮"
              className="text-[36px] font-extrabold font-display text-[var(--text-primary)] tracking-tight"
            />
            <p className="text-[11px] text-[var(--text-secondary)] mt-1">
              <Trans
                i18nKey="network.partnersSummary"
                values={{
                  partners: totals.partners,
                  deposit: totals.deposit.toLocaleString(locale),
                }}
                components={{ b: <strong /> }}
              />
            </p>
          </div>

          {/* Referral link row */}
          <div
            className={`rounded-2xl p-3 flex items-center gap-3 ${
              HAS_ACTIVE_SLOT ? '' : 'opacity-50'
            }`}
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
            }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono">
                {t('network.refLinkLabel')}
              </p>
              <p className="text-[12px] text-[var(--text-primary)] font-mono truncate">
                {HAS_ACTIVE_SLOT ? REF_LINK : t('network.activateSlotToOpen')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              disabled={!HAS_ACTIVE_SLOT}
              className={`h-9 w-9 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                HAS_ACTIVE_SLOT ? 'active:scale-90' : 'cursor-not-allowed'
              }`}
              style={{
                background: HAS_ACTIVE_SLOT
                  ? 'color-mix(in oklab, var(--accent-primary) 20%, transparent)'
                  : 'var(--glass-fill)',
                border: HAS_ACTIVE_SLOT
                  ? '1px solid color-mix(in oklab, var(--accent-primary) 45%, transparent)'
                  : '1px solid var(--glass-stroke)',
                color: HAS_ACTIVE_SLOT
                  ? 'var(--accent-primary)'
                  : 'var(--text-disabled)',
              }}
            >
              {HAS_ACTIVE_SLOT ? (
                copied ? (
                  <Check size={14} strokeWidth={2.5} />
                ) : (
                  <Copy size={14} strokeWidth={2} />
                )
              ) : (
                <Lock size={14} strokeWidth={2} />
              )}
            </button>
          </div>
        </GlassSurface>
      </motion.div>

      {/* ─── PROGRESS BARS (Личный / Сеть / L1-30д) — между HERO и визуализацией ─── */}
      <motion.div variants={itemVariants}>
        <ProgressBars
          progress={progress}
          loading={progressLoading}
          expanded={progressExpanded}
          onToggle={toggleProgress}
        />
      </motion.div>

      {/* ─── TREE VISUALIZATION ENTRY ─── */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          tone="plasma"
          onClick={() => navigate('/network/tree')}
          className="p-4"
        >
          <div className="flex items-center gap-3">
            <div
              className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'color-mix(in oklab, var(--accent-secondary) 20%, transparent)',
                border:
                  '1px solid color-mix(in oklab, var(--accent-secondary) 45%, transparent)',
                boxShadow:
                  '0 0 22px -4px color-mix(in oklab, var(--accent-secondary) 60%, transparent)',
              }}
            >
              <TrendingUp
                size={18}
                strokeWidth={2}
                className="text-[var(--accent-secondary)]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                {t('network.visualization')}
              </p>
              <p className="text-[14px] font-display font-extrabold text-[var(--text-primary)] tracking-tight">
                {t('network.openMap')}
              </p>
              <p className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                {t('network.mapHint')}
              </p>
            </div>
            <ChevronDown
              size={18}
              strokeWidth={2}
              className="text-[var(--text-muted)] -rotate-90 flex-shrink-0"
            />
          </div>
        </ClickableCard>
      </motion.div>

      {/* ─── TEAM LIST ENTRY ─── */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          tone="lunar"
          onClick={() => navigate('/network/team')}
          className="p-4"
        >
          <div className="flex items-center gap-3">
            <div
              className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'color-mix(in oklab, var(--accent-primary) 20%, transparent)',
                border:
                  '1px solid color-mix(in oklab, var(--accent-primary) 45%, transparent)',
                boxShadow:
                  '0 0 22px -4px color-mix(in oklab, var(--accent-primary) 60%, transparent)',
              }}
            >
              <Users
                size={18}
                strokeWidth={2}
                className="text-[var(--accent-primary)]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                {t('network.list')}
              </p>
              <p className="text-[14px] font-display font-extrabold text-[var(--text-primary)] tracking-tight">
                {t('network.myTeam')}
              </p>
              <p className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                {t('network.teamHint')}
              </p>
            </div>
            <ChevronDown
              size={18}
              strokeWidth={2}
              className="text-[var(--text-muted)] -rotate-90 flex-shrink-0"
            />
          </div>
        </ClickableCard>
      </motion.div>

      {/* ─── STATUS CARD (Imperator gate) ─── */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          tone="nebula"
          className="p-4"
        >
          <div className="flex items-center gap-3">
            <div
              className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  'color-mix(in oklab, var(--accent-tertiary) 18%, transparent)',
                border:
                  '1px solid color-mix(in oklab, var(--accent-tertiary) 40%, transparent)',
                boxShadow:
                  '0 0 22px -4px color-mix(in oklab, var(--accent-tertiary) 55%, transparent)',
              }}
            >
              <Shield
                size={18}
                strokeWidth={2}
                className="text-[var(--accent-tertiary)]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                {t('network.leaderStatus')}
              </p>
              {(() => {
                // Динамика из реальных данных API. Используем threshold
                // на каждой линии (приходит из /referrals/tree) и количество
                // активных прямых рефералов чтобы построить прогресс.
                const totalLines = lines.length; // 10
                const openCount = lines.filter((l) => l.status === 'active').length;
                const nextClosed = lines.find((l) => l.status !== 'active');
                return (
                  <>
                    <p className="text-[14px] font-display font-extrabold text-[var(--text-primary)] tracking-tight">
                      {t('network.openedOfN', { open: openCount, total: totalLines })}
                    </p>
                    {nextClosed && (
                      <p className="text-[11px] text-[var(--text-muted)] font-mono mt-0.5">
                        {t('network.nextLineHint', { level: nextClosed.level })}
                      </p>
                    )}
                  </>
                );
              })()}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/progress');
                }}
                className="text-[11px] text-[var(--accent-secondary)] mt-1 inline-flex items-center gap-1"
              >
                {t('network.details')}
              </button>
            </div>
          </div>
        </ClickableCard>
      </motion.div>

      {/* ─── LINES ACCORDION ─── */}
      <motion.div variants={itemVariants} className="space-y-2.5">
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono font-bold">
            {t('network.linesHeader')}
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono">
            {t('network.linesStats', {
              open: totals.open,
              grace: totals.grace,
              locked: 10 - totals.open - totals.grace,
            })}
          </span>
        </div>

        {lines.map((line) => (
          <LineRowView
            key={line.level}
            line={line}
            activeDirectRefs={activeDirectRefs}
            expanded={expanded === line.level}
            onToggle={() => handleToggle(line.level)}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Accordion row
// ────────────────────────────────────────────────────────────────────
function LineRowView({
  line,
  activeDirectRefs,
  expanded,
  onToggle,
}: {
  line: LineRow;
  activeDirectRefs: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const disabled = line.status === 'locked';
  // Сколько ещё активных рефов в 1-й линии нужно пригласить,
  // чтобы эта линия открылась. Показываем только для locked.
  const lackingRefs = Math.max(0, line.threshold - activeDirectRefs);

  const toneColor =
    line.status === 'active'
      ? 'var(--status-positive)'
      : line.status === 'grace'
        ? 'var(--status-warning)'
        : 'var(--text-disabled)';

  const accentBg =
    line.status === 'active'
      ? 'color-mix(in oklab, var(--status-positive) 14%, transparent)'
      : line.status === 'grace'
        ? 'color-mix(in oklab, var(--status-warning) 14%, transparent)'
        : 'var(--glass-fill)';

  return (
    <div
      className="lg-surface rounded-[22px] overflow-hidden"
      style={{
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <button
        type="button"
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        className="w-full text-left px-4 py-3 flex items-center gap-3 transition-transform active:scale-[0.99]"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        {/* Level indicator */}
        <div
          className="h-11 w-11 rounded-2xl flex items-center justify-center flex-shrink-0 font-mono font-extrabold text-[13px]"
          style={{
            background: accentBg,
            color: toneColor,
            border: `1px solid color-mix(in oklab, ${toneColor} 38%, transparent)`,
          }}
        >
          L{line.level}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-extrabold text-[var(--text-primary)]">
                {t('network.pctPayout', { pct: line.pct })}
              </span>
              {line.status === 'active' && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1.5px] text-[8px] font-mono font-extrabold uppercase tracking-[0.12em]"
                  style={{
                    background:
                      'color-mix(in oklab, var(--status-positive) 18%, transparent)',
                    color: 'var(--status-positive)',
                    border:
                      '1px solid color-mix(in oklab, var(--status-positive) 40%, transparent)',
                  }}
                >
                  <TrendingUp size={9} strokeWidth={3} />
                  {t('network.live')}
                </span>
              )}
              {line.status === 'grace' && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1.5px] text-[8px] font-mono font-extrabold uppercase tracking-[0.12em]"
                  style={{
                    background:
                      'color-mix(in oklab, var(--status-warning) 18%, transparent)',
                    color: 'var(--status-warning)',
                    border:
                      '1px solid color-mix(in oklab, var(--status-warning) 40%, transparent)',
                  }}
                >
                  <Clock size={9} strokeWidth={3} />
                  {t('network.graceBadge', { days: line.graceDaysLeft })}
                </span>
              )}
              {line.status === 'locked' && (
                <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1.5px] text-[8px] font-mono font-bold uppercase tracking-[0.12em] text-[var(--text-disabled)] border border-[var(--glass-stroke)]">
                  <Lock size={9} strokeWidth={3} />
                  {t('network.status')}
                </span>
              )}
            </div>
            {!disabled && (
              <ChevronDown
                size={14}
                strokeWidth={2.4}
                className="text-[var(--text-muted)] transition-transform flex-shrink-0"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                }}
              />
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-[var(--text-muted)]">
            <span className="inline-flex items-center gap-1">
              <Users size={9} strokeWidth={2.4} />
              {line.partners}
            </span>
            <span className="tabular-nums">
              {t('network.volume', { amount: line.depositUsdt.toLocaleString(locale) })}
            </span>
            <span
              className="tabular-nums font-extrabold ml-auto"
              style={{ color: line.status === 'locked' ? 'inherit' : toneColor }}
            >
              {line.earnedUsdt > 0 ? `+${line.earnedUsdt.toFixed(2)}` : '—'}
            </span>
          </div>

          {/* Чёткая инструкция как разблокировать закрытую линию.
              Всегда видима под header'ом (не в аккордеоне), чтоб
              юзер сразу понимал чего не хватает. */}
          {disabled && line.threshold > 0 && (
            <div
              className="mt-2 flex items-start gap-1.5 text-[10px]
                         leading-snug"
              style={{ color: 'var(--text-secondary)' }}
            >
              <Lock
                size={10}
                strokeWidth={2.4}
                className="mt-[1px] flex-shrink-0 text-[var(--text-muted)]"
              />
              <span>
                <Trans
                  i18nKey="network.needRefs"
                  values={{ threshold: line.threshold, current: activeDirectRefs }}
                  components={{ b: <b style={{ color: 'var(--text-primary)' }} /> }}
                />
                {lackingRefs > 0 && (
                  <>
                    {' '}
                    <Trans
                      i18nKey="network.inviteMore"
                      values={{ count: lackingRefs }}
                      components={{ b: <b style={{ color: 'var(--accent-primary)' }} /> }}
                    />
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </button>

      <AnimatePresence>
        {expanded && !disabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 pt-0 border-t border-[var(--glass-stroke)]">
              <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono pt-2 mb-1.5">
                {t('network.topPartners')}
              </p>
              {line.topPartners.length > 0 ? (
                <ul className="space-y-1.5">
                  {line.topPartners.map((p) => (
                    <li
                      key={p.label}
                      className="flex items-center justify-between text-[12px]"
                    >
                      <span className="text-[var(--text-primary)] font-semibold">
                        {p.label}
                      </span>
                      <span className="text-[var(--text-muted)] font-mono tabular-nums">
                        {p.deposit.toLocaleString(locale)} · {p.days}{i18n.language === 'ru' ? 'д' : 'd'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-[var(--text-muted)] font-mono py-1.5">
                  {t('network.noPartnersOnLine')}
                </p>
              )}
            </div>
          </motion.div>
        )}
        {expanded && line.status === 'grace' && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div
              className="px-4 py-2.5 text-[11px]"
              style={{
                background:
                  'color-mix(in oklab, var(--status-warning) 8%, transparent)',
                borderTop: '1px solid var(--glass-stroke)',
                color: 'var(--status-warning)',
              }}
            >
              <Clock size={11} className="inline mr-1.5" strokeWidth={2.4} />
              <Trans
                i18nKey="network.graceHint"
                values={{ days: line.graceDaysLeft }}
                components={{ b: <strong /> }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
